import { DraftJSFormat, Entity, EntityRange, InlineStyleRange, LexicalFormat, LexicalNode } from "components/ContentEditor/converter/types";

// Helper to extract inline styles from a formatted text node
const extractInlineStylesFromTextNode = (textNode: LexicalNode, offset: number): InlineStyleRange[] => {
	const styles: InlineStyleRange[] = [];
	if (typeof textNode.format === "number" && textNode.format > 0) {
		const length = (textNode.text || "").length;
		if (textNode.format & 1) {
			styles.push({ offset, length, style: "BOLD" });
		}
		if (textNode.format & 2) {
			styles.push({ offset, length, style: "ITALIC" });
		}
	}
	return styles;
};

// Helper to process a text node and extract text with inline styles
const processTextNode = (textNode: LexicalNode, currentOffset: number): { text: string; inlineStyleRanges: InlineStyleRange[] } => {
	const text = textNode.text || "";
	const inlineStyleRanges = extractInlineStylesFromTextNode(textNode, currentOffset);
	return { text, inlineStyleRanges };
};

// Helper to extract all text from a link node and track inline styles
const extractLinkTextAndStyles = (linkNode: LexicalNode): { text: string; inlineStyleRanges: InlineStyleRange[] } => {
	let text = "";
	const inlineStyleRanges: InlineStyleRange[] = [];

	if (linkNode.children && Array.isArray(linkNode.children)) {
		for (const child of linkNode.children) {
			if (child.type === "text") {
				const startOffset = text.length;
				const { text: childText, inlineStyleRanges: childStyles } = processTextNode(child, startOffset);
				text += childText;
				inlineStyleRanges.push(...childStyles);
			}
		}
	}

	return { text, inlineStyleRanges };
};

// Helper to process a link node and add to block content
const processLinkNodeForBlock = (
	linkNode: LexicalNode,
	currentText: string,
	entityMap: Record<number, Entity>,
	entityKey: number
): {
	text: string;
	entityRange: EntityRange;
	linkStyles: InlineStyleRange[];
	newEntityKey: number;
} => {
	const { text: linkText, inlineStyleRanges: linkStyles } = extractLinkTextAndStyles(linkNode);
	const startOffset = currentText.length;

	const entityRange: EntityRange = {
		offset: startOffset,
		length: linkText.length,
		key: entityKey,
	};

	entityMap[entityKey] = {
		type: "LINK",
		mutability: "MUTABLE",
		data: {
			url: linkNode.url,
			title: linkText,
			targetOption: (linkNode.target as string) || "_blank",
		},
	};

	return {
		text: linkText,
		entityRange,
		linkStyles,
		newEntityKey: entityKey + 1,
	};
};

// Helper to process image nodes in paragraph content
const processImageNodeForBlock = (
	imageNode: LexicalNode,
	entityMap: Record<number, Entity>,
	entityKey: number
): { text: string; entityRange: EntityRange; newEntityKey: number } => {
	const entityRange: EntityRange = {
		offset: 0, // Will be set by caller
		length: 7,
		key: entityKey,
	};

	entityMap[entityKey] = {
		type: "IMAGE",
		mutability: "MUTABLE",
		data: {
			src: imageNode.src,
			alt: imageNode.altText || "",
			width: imageNode.width,
			height: imageNode.height,
		},
	};

	return {
		text: "[image]",
		entityRange,
		newEntityKey: entityKey + 1,
	};
};

// Helper to extract text and entities from list item paragraph content
const extractListItemContent = (
	listItem: LexicalNode,
	entityMap: Record<number, Entity>,
	entityKeyRef: { value: number }
): {
	text: string;
	inlineStyleRanges: InlineStyleRange[];
	entityRanges: EntityRange[];
} => {
	let text = "";
	const inlineStyleRanges: InlineStyleRange[] = [];
	const entityRanges: EntityRange[] = [];

	if (listItem.children && Array.isArray(listItem.children)) {
		for (const child of listItem.children) {
			// If child is a paragraph, descend into it to find text/link nodes
			if (child.type === "paragraph" && child.children && Array.isArray(child.children)) {
				for (const contentNode of child.children) {
					if (contentNode.type === "text") {
						const { text: childText, inlineStyleRanges: childStyles } = processTextNode(contentNode, text.length);
						text += childText;
						inlineStyleRanges.push(...childStyles);
					} else if (contentNode.type === "link" && contentNode.url) {
						const {
							text: linkText,
							entityRange,
							linkStyles,
							newEntityKey,
						} = processLinkNodeForBlock(contentNode, text, entityMap, entityKeyRef.value);
						text += linkText;
						entityRanges.push(entityRange);
						entityKeyRef.value = newEntityKey;
						for (const style of linkStyles) {
							inlineStyleRanges.push({
								offset: entityRange.offset + style.offset,
								length: style.length,
								style: style.style,
							});
						}
					}
				}
			} else if (child.type === "text") {
				// Fallback: handle direct text nodes
				const { text: childText, inlineStyleRanges: childStyles } = processTextNode(child, text.length);
				text += childText;
				inlineStyleRanges.push(...childStyles);
			} else if (child.type === "link" && child.url) {
				// Fallback: handle direct link nodes
				const { text: linkText, entityRange, linkStyles, newEntityKey } = processLinkNodeForBlock(child, text, entityMap, entityKeyRef.value);
				text += linkText;
				entityRanges.push(entityRange);
				entityKeyRef.value = newEntityKey;
				for (const style of linkStyles) {
					inlineStyleRanges.push({
						offset: entityRange.offset + style.offset,
						length: style.length,
						style: style.style,
					});
				}
			}
		}
	}

	return { text, inlineStyleRanges, entityRanges };
};

/**
 * Convert Lexical format to draft-js format
 */
export function lexicalToDraftJS(lexicalContent: string | LexicalFormat): DraftJSFormat {
	try {
		const parsed: LexicalFormat = typeof lexicalContent === "string" ? JSON.parse(lexicalContent) : lexicalContent;
		const children = parsed.root?.children || [];

		let blockKey = 0;
		let entityKey = 0;
		const entityMap: Record<number, Entity> = {};

		const blocks: Array<DraftJSFormat["blocks"][0]> = [];

		for (const node of children) {
			if (node.type === "image" && node.src) {
				// Handle image nodes as atomic blocks
				blocks.push({
					key: `block_${blockKey++}`,
					text: " ",
					type: "atomic",
					depth: 0,
					inlineStyleRanges: [],
					entityRanges: [
						{
							offset: 0,
							length: 1,
							key: entityKey,
						},
					],
					data: {},
				});

				entityMap[entityKey] = {
					type: "IMAGE",
					mutability: "MUTABLE",
					data: {
						src: String(node.src),
						alt: String(node.altText || ""),
						width: node.width ? String(node.width) : "auto",
						height: node.height ? String(node.height) : "auto",
					},
				};

				entityKey++;
			} else if (node.type === "list" || node.type === "ul" || node.type === "ol") {
				// Process list items
				const listType =
					(node.type === "list" ? node.listType : node.type === "ul" ? "bullet" : "number") === "bullet"
						? "unordered-list-item"
						: "ordered-list-item";

				for (const listItem of node.children ?? []) {
					const entityKeyRef = { value: entityKey };
					const { text, inlineStyleRanges, entityRanges } = extractListItemContent(listItem, entityMap, entityKeyRef);
					entityKey = entityKeyRef.value;

					blocks.push({
						key: `block_${blockKey++}`,
						text,
						type: listType,
						depth: 0,
						inlineStyleRanges,
						entityRanges,
						data: {},
					});
				}
			} else {
				// Process non-list nodes (paragraphs and headings)
				const entityKeyRef = { value: entityKey };
				let text = "";
				const inlineStyleRanges: InlineStyleRange[] = [];
				const entityRanges: EntityRange[] = [];

				if (node.children && Array.isArray(node.children)) {
					for (const child of node.children) {
						if (child.type === "text") {
							const { text: childText, inlineStyleRanges: childStyles } = processTextNode(child, text.length);
							text += childText;
							inlineStyleRanges.push(...childStyles);
						} else if (child.type === "link" && child.url) {
							const {
								text: linkText,
								entityRange,
								linkStyles,
								newEntityKey,
							} = processLinkNodeForBlock(child, text, entityMap, entityKeyRef.value);
							text += linkText;
							entityRanges.push(entityRange);
							entityKeyRef.value = newEntityKey;
							for (const style of linkStyles) {
								inlineStyleRanges.push({
									offset: entityRange.offset + style.offset,
									length: style.length,
									style: style.style,
								});
							}
						} else if (child.type === "image" && child.src) {
							const { text: imgText, entityRange, newEntityKey } = processImageNodeForBlock(child, entityMap, entityKeyRef.value);
							const startOffset = text.length;
							text += imgText;
							entityRanges.push({
								offset: startOffset,
								length: entityRange.length,
								key: entityRange.key,
							});
							entityKeyRef.value = newEntityKey;
						}
					}
				}

				entityKey = entityKeyRef.value;

				// Determine block type based on node type and level
				let type = "unstyled";
				if (node.type === "heading") {
					const level = (node.level || node.tag) as "h1" | "h2" | "h3" | undefined;
					if (level === "h2") {
						type = "header-two";
					} else if (level === "h3") {
						type = "header-three";
					} else {
						type = "header-two";
					}
				}

				blocks.push({
					key: `block_${blockKey++}`,
					text,
					type,
					depth: 0,
					inlineStyleRanges,
					entityRanges,
					data: {},
				});
			}
		}

		return {
			blocks,
			entityMap,
		};
	} catch (e) {
		console.error("Error converting Lexical to draft-js:", e);
		return {
			blocks: [],
			entityMap: {},
		};
	}
}
